import type { ManualChapter } from '../types';

/** Rozdział instrukcji: Planista FTL (28.10.2026, S-28.10-02). */
export const chapter: ManualChapter = {
  key: 'planner',
  title: 'Planista FTL',
  audience:
    'Dla planistów FTL — opiekunów pojazdów, którzy szukają aut do zleceń, dodają warianty i pilnują planu swojej floty.',
  intro:
    'Planista pracuje na liście zleceń, karcie zlecenia, rekomendacjach pojazdów, karcie pojazdu i harmonogramie floty. System liczy dolot, terminy, czas pracy kierowcy i koszty oraz podpowiada auta, ale niczego sam nie obsadza: planista wskazuje warianty, a ostatecznie obsadza Team Leader.',
  sections: [
    // ─────────────────────────────────────────────────────────────── rola
    {
      id: 'twoja-rola',
      title: 'Twoja rola i zasady pracy',
      blocks: [
        {
          kind: 'text',
          text: 'Jako planista FTL dobierasz pojazdy do zleceń z puli „Gotowe do planowania”. Dodajesz auta jako warianty (wstępne wskazania), odrzucasz podpowiedzi, które nie pasują, i pilnujesz, żeby Twoje auta miały pracę. Ostatnie słowo — „Obsadź” — należy do Team Leadera FTL i administracji.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'System przygotowuje, człowiek zatwierdza. Rekomendacje, werdykty „zdąży” i „dowiezie”, koszty i marża to podpowiedzi — decyzję o wariancie i obsadzeniu podejmuje człowiek. Nic nie trafia samo do TMS ani na giełdę.',
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Gdy danych brakuje, system pokazuje „—” albo „brak danych” z powodem. Nie zgaduje: brak pozycji auta, ładowności czy wpisu ADR nigdy nie jest zamieniany na wartość wymyśloną.',
        },
        {
          kind: 'task',
          title: 'Ustaw, których aut jesteś opiekunem',
          intro: 'Zrób to raz, na początku pracy — od tego zależy „Tylko moje pojazdy” i Twoje widoki.',
          steps: [
            'Kliknij swoją nazwę w prawym górnym rogu, żeby otworzyć menu użytkownika.',
            'W polu „Jestem opiekunem FTL” wybierz siebie (opiekuna takiego, jak w kartotece pojazdów TMS).',
            'Zamknij menu — listy floty, harmonogram i rekomendacje z włączonym „Tylko moje pojazdy” pokażą teraz Twoje auta.',
          ],
          outcome:
            '„Tylko moje pojazdy” to filtr widoku, nie uprawnienie — jednym kliknięciem wrócisz do całej floty. Planista zaczyna z tym przełącznikiem włączonym.',
        },
        {
          kind: 'note',
          tone: 'system',
          when: { on: ['caretakerAuditLog'] },
          text: 'Każda zmiana wskazania „Jestem opiekunem FTL” trafia do dziennika działań: kto, kiedy, z czego na co. Zapis bez zmiany niczego nie dopisuje.',
        },
        {
          kind: 'note',
          tone: 'warning',
          when: { on: ['variantScopeOwnVehicles'] },
          text: 'Gdy masz wskazanego opiekuna, warianty dodajesz i zwalniasz tylko przy swoich autach, i tylko przy nich składasz wniosek o odpięcie. Przy cudzych autach przycisków nie ma, a próba kończy się odmową z powodem. Bez wskazanego opiekuna ograniczenia nie ma.',
        },
        {
          kind: 'text',
          when: { off: ['variantScopeOwnVehicles'] },
          text: 'Warianty możesz dodawać i zwalniać przy wszystkich autach floty, także cudzych — uzgadniaj to z ich opiekunami.',
        },
      ],
    },

    // ─────────────────────────────────────────────────────── pulpit planisty
    {
      id: 'pulpit-planisty',
      title: 'Pulpit planisty',
      menu: ['ftl-planner-dashboard'],
      when: { on: ['plannerDashboard'] },
      blocks: [
        {
          kind: 'text',
          text: 'Pulpit planisty to Twój ekran startowy. Zbiera liczby dla Twoich aut: warianty czekające na decyzję, z terminem mijającym dziś, po terminie i w konflikcie w czasie; zlecenia do obsadzenia w Twoim regionie (tam, gdzie zwalniają się Twoje pojazdy); pojazdy wolne teraz, zajęte i bez znanej pozycji; nieprzeczytane powiadomienia i wnioski o odpięcie czekające na decyzję.',
        },
        {
          kind: 'image',
          image: { guide: 'plannerDashboard', step: 1 },
          caption: 'Pulpit planisty — liczy pojazdy opiekuna wskazanego w menu użytkownika.',
        },
        {
          kind: 'task',
          title: 'Zacznij dzień od pulpitu',
          steps: [
            'Otwórz „Pulpit planisty” w menu.',
            'Sprawdź, czy u góry stoi właściwy opiekun — bez niego liczby „moich pojazdów” są puste.',
            'Kliknij kafelek z wariantami po terminie albo w konflikcie i rozstrzygnij je na liście zleceń.',
            'Kliknij „Wolne teraz”, żeby zobaczyć wolne auta w harmonogramie floty, a potem kafelek zleceń do obsadzenia w regionie.',
          ],
          outcome: 'Każda liczba otwiera listę z tym samym filtrem, którym ją policzono.',
        },
      ],
    },

    // ──────────────────────────────────────────── Transporeon — brak w TMS
    {
      id: 'transporeon-brak-w-tms',
      title: 'Transporeon — brak w TMS',
      menu: ['transporeon-missing'],
      when: { on: ['transporeonImport'] },
      blocks: [
        {
          kind: 'text',
          text: 'Kafelek „Transporeon — brak w TMS” na pulpicie planisty i na pulpicie Team Leadera liczy transporty, które zleceniodawca przydzielił w Transporeonie, a których po godzinie nadal nie ma w TMS pod tym numerem. Kliknięcie otwiera listę: zleceniodawca, numer z Transporeonu, relacja (kraj, kod, miejscowość), okno załadunku i od kiedy transportu brak w TMS. Godziny są w czasie polskim. Lista jest tylko do odczytu — transport znika z niej, gdy pojawi się w TMS, zostanie anulowany albo minie jego okno.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Gdy dane z Transporeonu są starsze niż godzina, pod liczbą stoi „Dane z Transporeonu z dnia …” — liczba to wtedy stan z tamtej chwili, nie bieżący.',
        },
        {
          kind: 'task',
          title: 'Sprawdź transport, którego nie ma w TMS',
          steps: [
            'Na pulpicie kliknij kafelek „Transporeon — brak w TMS” (albo otwórz pozycję o tej nazwie w menu).',
            'Znajdź transport po numerze z Transporeonu i oknie załadunku.',
            'Zapytaj dział przyjęcia zleceń, czy zlecenie trafi do TMS — dopiero wtedy da się je zaplanować.',
          ],
          outcome: 'Po pojawieniu się zlecenia w TMS transport znika z listy i z liczby kafelka.',
        },
      ],
    },

    // ─────────────────────────────────────────────────────────── bank zleceń
    {
      id: 'lista-zlecen',
      title: 'Lista zleceń (bank zleceń)',
      menu: ['user-offers'],
      blocks: [
        {
          kind: 'text',
          text: '„Zlecenia” to wspólny bank zleceń z TMS i prezleceń. Planista pracuje głównie na zleceniach w stanach „Gotowe do planowania” i „Wstępnie planowane”. Nad listą są filtry: wyszukiwarka („Numer, miasto, kod”), „Widok działu”, „Stan planowania” i „Znaczniki”; kolejne dodasz przyciskiem „Dodaj filtr” (np. „Tylko zagrożone”, „Status w TMS”). Przełącznik „Tylko moje zlecenia” zawęża listę do zleceń Twoich aut.',
        },
        {
          kind: 'image',
          image: { shot: 'bank' },
          caption: 'Lista zleceń z filtrami stanu planowania i znaczników.',
        },
        {
          kind: 'task',
          title: 'Znajdź zlecenia do zaplanowania',
          steps: [
            'Otwórz „Zlecenia” w menu.',
            'W filtrze „Stan planowania” wybierz „Gotowe do planowania” (i ewentualnie „Wstępnie planowane”).',
            'Posortuj po załadunku albo wpisz w wyszukiwarkę miasto lub kod pocztowy.',
            'Kliknij wiersz, żeby otworzyć kartę zlecenia i rekomendacje pojazdów.',
          ],
        },
        {
          kind: 'terms',
          title: 'Filtr „Znaczniki”',
          items: [
            {
              term: 'Domyślnie (bez czynności wózkowych)',
              meaning:
                'Czynności wózkowe to osobne zlecenia w TMS (rozładunek wózkiem przy zleceniu głównym), nie transport — lista ich domyślnie nie pokazuje. Można je pokazać albo wybrać tylko je.',
            },
            {
              term: '„Z alertem przeplanowania”',
              meaning: 'Obsadzone zlecenia, przy których system widzi lepsze albo pewniejsze auto.',
              when: { off: ['replanAlertMasterOnly'] },
            },
            {
              term: '„Straciły pojazd”',
              meaning:
                'Czerwona lista: zlecenia, które miały auto, straciły je i od tamtej pory nie wyszły z planowania. Znikają po ponownym obsadzeniu.',
            },
            {
              term: '„Bez wariantu”',
              when: { on: ['bankFtlFilters'] },
              meaning: 'Zlecenia w planowaniu, przy których nikt jeszcze nie wskazał auta.',
            },
            {
              term: '„Konflikt wariantów w czasie” i „Wariant po terminie”',
              when: { on: ['variantValidity'] },
              meaning: 'Zlecenia, których wariant nachodzi w czasie na inne zlecenie tego auta albo minął jego termin ważności.',
            },
          ],
        },
        {
          kind: 'list',
          title: 'Dodatkowe filtry listy',
          when: { on: ['bankFtlFilters'] },
          items: [
            'Kilka stanów planowania naraz.',
            'Załadunek w ciągu 1, 3, 7 albo 14 dni albo od–do (dni w Polsce).',
            'Kraj załadunku i rozładunku, typ pojazdu (JUMBO, MEGA, STANDARD, częściowe) i wymagania (ADR, fix, wózek).',
            'Opiekun FTL i „Tylko wymagające obsadzenia” — gotowe i wstępnie planowane bez auta.',
          ],
        },
        {
          kind: 'text',
          when: { on: ['bankFtlColumns'] },
          text: 'Lista pokazuje więcej w tych samych kolumnach: „Załadunek” ma okno od–do, pod relacją stoi liczba punktów i wymagania, a w „Pojeździe” przy zleceniu bez auta — aktywne warianty z numerem auta i tym, kto je dodał.',
        },
        {
          kind: 'text',
          when: { on: ['bankRateTypeColumns'] },
          text: 'Stawka zlecenia i wymagany typ pojazdu (z wózkiem, ADR, fix) mają osobne kolumny „Stawka” i „Typ pojazdu”, sortowanie po kwocie i te same kolumny w eksporcie.',
        },
        {
          kind: 'text',
          when: { on: ['priceStatistics'] },
          text: 'Kolumna „€/km” pokazuje cenę klienta za kilometr na tle historii zleceń z TMS (relacja, klient, spółka). To tylko informacja — nie zmienia kolejności ani werdyktów.',
        },
        {
          kind: 'text',
          when: { on: ['bankRoleDefaultViews'] },
          text: 'Pierwszy widok listy zależy od roli: planista widzi „Gotowe do planowania” i „Wstępnie planowane” z załadunkiem w ciągu 7 dni. W menu „Widoki” pozycja „Widok domyślny” przywraca go w każdej chwili.',
        },
        {
          kind: 'task',
          title: 'Zapisz własny widok listy',
          when: { on: ['bankSavedViews'] },
          steps: [
            'Ustaw filtry i sortowanie tak, jak chcesz z nich korzystać.',
            'Kliknij „Widoki” nad listą i wybierz „Zapisz bieżący widok…”.',
            'W oknie „Zapisz widok listy” wpisz „Nazwa widoku” i kliknij „Zapisz” — widok wybierzesz potem z tego samego menu, także na innym komputerze. Ta sama nazwa zastępuje zapisany widok.',
          ],
          outcome: 'Widoki są prywatne i zapisane na Twoim koncie.',
        },
        {
          kind: 'text',
          when: { on: ['bankMyFleetView'] },
          text: 'Pozycja menu „Zlecenia mojej floty” pod „Zleceniami” to ta sama lista zawężona na stałe do zleceń obsadzonych Twoimi autami albo z ich aktywnym wariantem, z własnymi filtrami.',
        },
        {
          kind: 'text',
          when: { on: ['bankRowAssign'] },
          text: 'Przy zleceniu bez auta, ale z wariantami, w kolumnie „Pojazd” stoi „Obsadź…”: okno porównuje warianty obok siebie (dolot, koszt, marża, wolny od, zapas, uwagi). Obsadza z niego osoba z uprawnieniem do obsadzania — zwykle Team Leader.',
        },
      ],
    },

    // ─────────────────────────────────────────────────────── stany zlecenia
    {
      id: 'stany-zlecenia',
      title: 'Stany zlecenia i statusy w TMS',
      blocks: [
        {
          kind: 'terms',
          title: 'Stan planowania (kolumna „Stan”)',
          items: [
            { term: 'Prezlecenie', meaning: 'Informacja o możliwym transporcie przed pełnym potwierdzeniem; czeka na akceptację.' },
            { term: 'Wymaga uzupełnienia', meaning: 'Brakuje danych potrzebnych do planowania.' },
            { term: 'Gotowe do planowania', meaning: 'Aktywna pula FTL — zlecenie czeka na auto.' },
            {
              term: 'Wstępnie planowane',
              meaning: 'Zlecenie ma co najmniej jeden wariant (wskazane auto). Pierwszy wariant przestawia je tu samo.',
            },
            {
              term: 'Obsadzone flotą',
              meaning:
                'Auto zostało ostatecznie przypisane („Obsadź” albo obsadzenie wpisane w TMS). Pozostałe warianty zlecenia zwalniają się, a to samo auto traci kandydatury w innych zleceniach w tym samym czasie.',
            },
            {
              term: 'Obsadzone przewoźnikiem',
              meaning:
                'TMS ma przy zleceniu przewoźnika (podwykonawcę spoza Trade) i nie ma naszego auta. Ustawia to wyłącznie import z TMS; powrót do planowania robi człowiek, z powodem.',
            },
            { term: 'Przekazane do Trade', meaning: 'FTL oddał zlecenie do sprzedaży przewoźnikowi — dalej pracuje dział Trade.' },
            {
              term: 'Sprzedane przez Trade',
              meaning: 'Przy zleceniu Trade pojawił się w TMS przewoźnik albo numer pojazdu. Ustawia to import; sprzedane już nie wraca do FTL.',
            },
            { term: 'Nieaktualne', meaning: 'Wycofane, wygasłe albo świadomie zamknięte.' },
            {
              term: 'Zrealizowane',
              when: { on: ['tmsCompletedFinal'] },
              meaning:
                'Zlecenie zakończone w TMS (status Z). Import przenosi je tu od razu, z wpisem w historii; auto i przewoźnik zostają zapisane. Stan nie jest domyślnie widoczny na liście — wybierzesz go w filtrze stanów.',
            },
          ],
        },
        {
          kind: 'text',
          when: { off: ['tmsCompletedFinal'] },
          text: 'Zlecenie zakończone w TMS zostaje w swoim stanie planowania — o realizacji mówi kolumna statusu w TMS.',
        },
        {
          kind: 'terms',
          title: 'Status w TMS',
          items: [
            { term: 'W — nieobsadzone', meaning: 'TMS nie ma jeszcze wykonawcy. Tylko takie zlecenia trafiają do rekomendacji zleceń dla pojazdu.' },
            { term: 'P — obsadzone', meaning: 'TMS ma wykonawcę. Rekomendacje pokażą się jako alternatywy.' },
            { term: 'C — w realizacji', meaning: 'Zlecenie jest w drodze (to nie anulacja). Rekomendacji pojazdów nie ma.' },
            { term: 'Z — zrealizowane', meaning: 'Zlecenie zakończone. Rekomendacji pojazdów nie ma.' },
          ],
        },
        {
          kind: 'task',
          title: 'Zmień stan zlecenia',
          intro: 'Np. gdy zlecenie trzeba uzupełnić albo jest nieaktualne.',
          steps: [
            'Na karcie zlecenia w sekcji akcji rozwiń „Zmień stan” — lista pokazuje tylko dozwolone przejścia.',
            'Wybierz stan docelowy i wpisz powód, jeśli pole mówi „Powód (wymagany)”.',
            'Kliknij „Zmień”.',
          ],
          outcome:
            'Zmiana trafia do historii zlecenia. Zejście z „Wstępnie planowane” do „Gotowe do planowania” zwalnia warianty. Stanów „Obsadzone flotą”, „Obsadzone przewoźnikiem” i „Sprzedane przez Trade” nie ustawisz ręcznie.',
        },
      ],
    },

    // ─────────────────────────────────────────────────────── karta zlecenia
    {
      id: 'karta-zlecenia',
      title: 'Karta zlecenia',
      blocks: [
        {
          kind: 'text',
          text: 'Karta zlecenia zbiera wszystko o jednym zleceniu: punkty trasy z oknami czasowymi, ładunek i wymagany typ pojazdu, stan planowania i status w TMS, alerty, warianty obsadzenia, rekomendowane pojazdy, akcje i historię zmian.',
        },
        {
          kind: 'image',
          image: { shot: 'order-card' },
          caption: 'Karta zlecenia — u góry stan, terminy i pojazd, pod nimi „Do zrobienia” i kolejne sekcje karty.',
        },
        {
          kind: 'text',
          when: { on: ['sourcePreview'] },
          text: 'Skąd się wzięło zlecenie: na karcie prezlecenia obok danych stoi mail klienta i jego załączniki (otwiera je przycisk „Pokaż źródło”, przy prezleceniach z dokumentów są od razu), każdy w osobnej zakładce. PDF i zdjęcie zobaczysz na miejscu, arkusz i dokument pobierzesz przyciskiem „Pobierz oryginał”. Źródło jest tylko do odczytu — gdy czegoś brakuje albo coś się nie zgadza, poproś CS albo Administrację o poprawkę.',
        },
        {
          kind: 'terms',
          title: 'Co jest na karcie',
          items: [
            { term: '„Punkty trasy (N)”', meaning: 'Załadunki, punkty pośrednie i rozładunki z oknami w czasie polskim.' },
            {
              term: '„Warianty obsadzenia (aktywne: N z M)”',
              meaning: 'Auta wskazane do zlecenia, kto i kiedy je dodał, oraz zwolnione warianty z powodem.',
            },
            { term: '„Rekomendowane pojazdy (wg dolotu)”', meaning: 'Lista aut policzona na żądanie — opis niżej.' },
            { term: '„Zgłoś ryzyko”', meaning: 'Oznacza zlecenie jako zagrożone, z powodem; „Wycofaj zgłoszenie ryzyka” zdejmuje oznaczenie.' },
            { term: '„Historia zmian”', meaning: 'Kto, kiedy i co zmienił — operator, import z TMS albo reguła systemu.' },
          ],
        },
        {
          kind: 'text',
          when: { on: ['orderWindowLayout'] },
          text: 'Karta ma układ z nagłówkiem (numer, relacja, liczba punktów), paskiem etapów zlecenia, paskiem podsumowania ze stanem planowania, statusem w TMS i giełdami oraz sekcjami 1–7. Przy polach stoi znacznik pochodzenia (TMS, prezlecenie, wpis operatora, wyliczenie). Treść i akcje są te same.',
        },
        {
          kind: 'text',
          when: { on: ['orderHistoryCollapsed'] },
          text: '„Historia zmian” jest zwinięta do podsumowania (liczba wpisów, ostatnia zmiana, liczba decyzji ludzi). Po rozwinięciu pokazuje całą historię strona po stronie, z filtrem: decyzje ludzi, system, import z TMS albo wszystko.',
        },
        {
          kind: 'text',
          when: { on: ['priceStatistics'] },
          text: 'Na karcie i nad rekomendowanymi pojazdami stoi zdanie o cenie klienta za kilometr na tle mediany relacji, klienta i spółki, np. „8% poniżej mediany relacji”. Przy mniej niż kilku zleceniach w historii — „za mało danych”.',
        },
        {
          kind: 'text',
          when: { on: ['orderChecklist'] },
          text: 'Panel „Do zrobienia” zbiera to, co już wiadomo o zleceniu: braki, ryzyko, utratę pojazdu, alert przeplanowania, brak wariantu, warianty czekające na decyzję. Przy obsadzonym zleceniu w toku jest lista czynności po obsadzeniu (Timeslot, alokacja pojazdu i kierowcy, awizacja odbiorcy, instrukcje dla kierowcy) — zaznaczasz wykonane, a kto i kiedy zostaje w historii. Nic nie wychodzi na zewnątrz.',
        },
        {
          kind: 'image',
          when: { on: ['orderChecklist'] },
          image: { guide: 'orderChecklist', step: 1 },
          caption: 'Panel „Do zrobienia” na karcie zlecenia.',
        },
      ],
    },

    // ─────────────────────────────────────────────────────────── rekomendacje
    {
      id: 'rekomendacje',
      title: 'Rekomendowane pojazdy dla zlecenia',
      blocks: [
        {
          kind: 'text',
          text: 'Rekomendacje liczą się na żądanie, bo dolot zależy od terminu załadunku i bieżącej pozycji aut. Lista jest ułożona po jednej zmierzonej wielkości — dolocie. Pozostałe składniki (kiedy auto się zwalnia, zapas czasu, ostrzeżenia, koszt) stoją obok, ale nie zmieniają domyślnej kolejności.',
        },
        {
          kind: 'task',
          title: 'Znajdź auto do zlecenia z rekomendacji',
          steps: [
            'Otwórz kartę zlecenia w stanie „Gotowe do planowania” albo „Wstępnie planowane”.',
            'W sekcji „Rekomendowane pojazdy (wg dolotu)” kliknij „Pokaż rekomendowane pojazdy”.',
            'Przejrzyj kolumny: „Dolot” z kosztem, „Wolny od”, „Zapas”, „Rozładunek (szacunek)”, „Uwagi”.',
            'Rozwiń „Na jakiej podstawie” przy aucie, żeby zobaczyć, skąd wzięła się pozycja, dolot i werdykty.',
            'Kliknij „Wariant” przy wybranym aucie (albo „Odrzuć”, jeśli auto nie pasuje).',
          ],
          outcome:
            'Auto dochodzi do wariantów zlecenia, a zlecenie przechodzi do „Wstępnie planowane”. Obsadza Team Leader.',
        },
        {
          kind: 'terms',
          title: 'Kolumny i oznaczenia przy pojeździe',
          items: [
            {
              term: 'Pozycja auta',
              meaning:
                'Ze świeżego GPS z eco-trucka (odczyt nie starszy niż 24 h) albo z rozładunku ostatniego zlecenia przed załadunkiem. Auto bez żadnego źródła nie dostaje wymyślonej pozycji — trafia do pominiętych z powodem.',
            },
            {
              term: '„Dolot” / „Dolot (szacunek)”',
              meaning:
                'Odległość z pozycji auta do załadunku — z mapy tras albo, bez niej, szacunek z linii prostej i mnożnika z danych floty. Pod spodem koszt dolotu w zł i marża.',
            },
            { term: '„Wolny od”', meaning: 'Kiedy auto skończy poprzednie obsadzenie (szacunek: przejazd, załadunek, rozładunek).' },
            {
              term: '„Zapas”',
              meaning:
                'Zapas czasu pracy kierowcy po wykonaniu zlecenia: najmniejsze z jazdy do limitu dobowego, 13 h dnia pracy i limitu tygodniowego, bez ulg. Brak danych to „—” z powodem, nie zero.',
            },
            { term: '„Rozładunek (szacunek)”', meaning: 'Szacowany przyjazd na ostatni rozładunek i czy mieści się w oknie.' },
            {
              term: '„zajęty: zlecenie X do hh:mm”',
              meaning:
                'Auto ma w tym czasie inne obsadzenie. Nie znika — stoi na dole listy (najwyżej 10), możesz je mimo to dodać jako wariant. To ostrzeżenie, nie blokada.',
            },
            {
              term: '„Nie pokazano N pojazdów: …”',
              meaning: 'Auta pominięte z powodem — wykluczone regułą albo bez pozycji. Każdy powód jest nazwany.',
            },
            { term: '„Odrzuć”', meaning: 'Odrzucenie podpowiedzi z powodem — auto zostaje na liście z oznaczeniem, wpis idzie do historii.' },
            {
              term: 'Konflikt z innym zleceniem',
              meaning: 'Auto jest już wariantem innego zlecenia — widać jego numer. Decyzja zostaje u planisty.',
            },
          ],
        },
        {
          kind: 'terms',
          title: 'Dodatkowe informacje przy rekomendacjach',
          items: [
            {
              term: 'Wykonalność',
              when: { on: ['recommendationFeasibility'] },
              meaning:
                '„mieści się”, „ciasny dolot” (mały zapas do początku okna załadunku), „tylko z ulgami”, „po oknie” albo „brak danych” — przepisane z werdyktów „zdąży” i „dowiezie”. Wykonalne stoją na górze, w grupie po dolocie. „Zapas” ma wtedy dwie liczby: „do okna” i „pracy”.',
            },
            {
              term: 'Szczegóły pary',
              when: { on: ['recommendationDetails'] },
              meaning:
                '„po zleceniu wolny od … w …”, koszt rozbity na paliwo, kierowcę, ciągnik i opłaty, kto dodał wariant i jakie inne warianty ma zlecenie, lista pominiętych z powodem i godzina przeliczenia.',
            },
            {
              term: 'Sortowanie nagłówkiem',
              when: { on: ['recommendationColumnSort'] },
              meaning:
                'Kliknięcie nagłówka (wykonalność, dolot, zapas, marża, rozładunek, wolny od) sortuje pokazaną listę; drugie — odwrotnie, trzecie — kolejność domyślna. Zajęte zostają na końcu.',
            },
            {
              term: 'Ocena 0–10',
              when: { on: ['pairScoring'] },
              meaning:
                'Liczba z jednym miejscem po przecinku z marży, wykonalności, dolotu względem trasy i priorytetu klienta; „z N z M składowych”, gdy części danych brak. Rozbicie w „Na jakiej podstawie”, przełącznik „Sortuj po ocenie”. Nie zmienia domyślnej kolejności i nikogo nie wyklucza.',
            },
            {
              term: '„Zajęte do” wg planu TMS',
              when: { on: ['occupiedUntilTmsPlan'] },
              meaning:
                'Przy zajętym aucie godzina „do …” to późniejsza z dwóch: szacunek albo plan TMS przy punktach, z podpisem źródła („wg planu TMS” / „szacunek”). Tylko informacja — odsiew i kolejność liczą szacunkiem.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Rekomendacja to podpowiedź. System nie ukrywa auta z powodu niepewnego werdyktu — „nie zdąży” albo „nie dowiezie” to ostrzeżenie, a decyzja zostaje u Ciebie i Team Leadera.',
        },
      ],
    },

    // ───────────────────────────────────────────── wykluczenia i ostrzeżenia
    {
      id: 'wykluczenia-ostrzezenia',
      title: 'Werdykty, wykluczenia i ostrzeżenia',
      blocks: [
        {
          kind: 'text',
          text: 'Przy każdej parze auto–zlecenie system rozróżnia wykluczenia (auto odpada z rekomendacji, trafia do „Nie pokazano” z powodem) i ostrzeżenia (auto zostaje na liście z uwagą). Brak danych nigdy nie wyklucza — daje ostrzeżenie albo „brak danych”.',
        },
        {
          kind: 'terms',
          title: 'Werdykty czasu',
          items: [
            {
              term: '„zdąży” / „nie zdąży”',
              meaning:
                'Czy auto dojedzie na początek okna załadunku: wyjazd po zwolnieniu, dolot i zegar kierowcy. Pewny jest tylko werdykt z kartoteki (nieobecność kierowcy) — pozostałe to szacunek.',
            },
            {
              term: '„dowiezie” / „nie dowiezie” / „dowiezie tylko z ulgami”',
              meaning:
                'Czy kierowca dowiezie przed końcem okna ostatniego rozładunku: dolot, załadunek, trasa, rozładunek, przerwy i odpoczynki wg czasu pracy kierowcy, bez ulg. „tylko z ulgami” — spóźnienie znika dopiero przy wydłużonej jeździe i skróconym odpoczynku.',
            },
            {
              term: '„brak danych”',
              meaning: 'Nie da się policzyć (np. brak pozycji, zlecenie za horyzontem 7 dni) — to nie jest „nie”.',
            },
            {
              term: '„po oknie” także na załadunku',
              when: { on: ['loadingWindowEnd'] },
              meaning:
                'Przyjazd na załadunek po KOŃCU okna załadunku to „po oknie” (ratunek tylko z ulg — „tylko z ulgami”). Zawsze szacunek: nikogo nie wyklucza i nie zmienia kolejności.',
            },
            {
              term: 'Ulgi z eco-trucka',
              when: { on: ['ecoTruckReliefs'] },
              meaning:
                'Wariant „tylko z ulgami” bierze pozostałe ulgi kierowcy z eco-trucka (wydłużenia jazdy do 10 h i skrócone odpoczynki do 9 h), gdy odczyt jest świeży — z podpisem „wg telematyki”.',
            },
            {
              term: 'Czas w punktach',
              meaning: 'Załadunek liczony jako 2,2 h, rozładunek 1,9 h — mediany postojów floty.',
            },
            {
              term: 'Czas w punktach wg miejsca',
              when: { on: ['stopTimesByPlace'] },
              meaning:
                'Czas postoju bierze się z mediany postojów GPS w tym miejscu (potem u zleceniodawcy, inaczej 2,2 h / 1,9 h), z punktem „Czas w punktach” w „Na jakiej podstawie”.',
            },
            {
              term: 'Tylko miarodajne miejsca',
              when: { on: ['stopTimesReliablePlace'] },
              meaning:
                'Mediana miejsca liczy się tylko przy dość wielu postojach i umiarkowanym rozrzucie, inaczej 2,2 h / 1,9 h. Gdy TMS mówi, że załadunek albo zlecenie już jest zrealizowane, system nie dolicza postoju po tym odczycie.',
            },
          ],
        },
        {
          kind: 'terms',
          title: 'Co wyklucza auto',
          items: [
            {
              term: 'Za mała ładowność',
              meaning:
                'Ładunek cięższy niż znana ładowność zestawu (auto z naczepą) — wykluczenie. Gdy ładowności nie znamy, system bierze wartość domyślną i tylko ostrzega.',
            },
            {
              term: 'Brak wymaganego wózka',
              meaning:
                'Zlecenie z wózkiem (np. „JUMBO TRUCK+FORKLIFT”) nie dostaje auta, którego zestaw wózka nie ma. Nieznany wózek nie wyklucza. Obsadzenia ręcznego nie blokuje.',
            },
            {
              term: 'Uprawnienia pojazdu i kierowcy (Blokada)',
              when: { on: ['recordExclusions'] },
              meaning:
                'Nieważny w dniu załadunku wpis typu Blokada (przegląd, OC, tachograf…) u pojazdu albo kierowcy, za mała znana ładowność, nieważne zezwolenie na pracę kierowcy i — przy obsadzeniu — pojazd poza eksploatacją wykluczają auto i blokują ręczne obsadzenie z powodem. Wpisy typu Alert tylko ostrzegają.',
            },
            {
              term: 'Czas pracy kierowcy',
              when: { on: ['workingTimeExclusion'] },
              meaning:
                'Gdy kierowca nie dowiezie przed końcem okna rozładunku nawet z pełną pulą ulg, a dane o czasie jazdy są świeże z telematyki — auto odpada z podpowiedzi z powodem. „tylko z ulgami” i brak danych dalej tylko ostrzegają.',
            },
            {
              term: 'ADR „nie”',
              when: { on: ['manualAdr'] },
              meaning:
                'Przy zleceniu z ADR wpis „nie” u pojazdu albo kierowcy wyklucza auto i blokuje obsadzenie. Brak wpisu — ostrzeżenie „uzupełnij w kartotece”.',
            },
          ],
        },
        {
          kind: 'text',
          when: { off: ['recordExclusions'] },
          text: 'Nieważne uprawnienia pojazdu i kierowcy, za mała ładowność i zezwolenie na pracę po terminie dają tylko ostrzeżenie — auto zostaje na liście.',
        },
        {
          kind: 'text',
          when: { off: ['manualAdr'] },
          text: 'Przy zleceniu z ADR rekomendacje ostrzegają, że brak danych o dopuszczeniu ADR pojazdu — sprawdź to sam.',
        },
        {
          kind: 'terms',
          title: 'Co tylko ostrzega',
          items: [
            {
              term: 'Niezgodny typ pojazdu',
              when: { on: ['vehicleTypeWarning'] },
              meaning:
                'Auto, którego typ nie obsłuży zlecenia (np. MEGA do zlecenia JUMBO), zostaje na liście z ostrzeżeniem i opisem w „Na jakiej podstawie”. JUMBO obsługuje JUMBO, MEGA, STANDARD i częściówki; MEGA — MEGA, STANDARD i częściówki do 13,6 m.',
            },
            {
              term: 'Kabotaż i „cooling off”',
              when: { on: ['cabotageWarnings'], off: ['cabotageByTms'] },
              meaning:
                'Ostrzeżenie o serii kabotaży (najwyżej 3 w 7 dni po przewozie międzynarodowym), przekroczeniu limitu i 4 dniach karencji w tym państwie, z punktem „Kabotaż” w „Na jakiej podstawie”. Kolejności nie zmienia.',
            },
            {
              term: 'Spółka auta',
              meaning: 'Auto drugiej spółki to tylko punkt „Spółka” w „Na jakiej podstawie”, bez uwagi.',
            },
            {
              term: 'Kierowca nieobecny, brak danych o kierowcy, „tylko z ulgami”',
              meaning: 'Ostrzeżenia przy aucie — decyzja należy do planisty.',
            },
            {
              term: 'Termin uprawnienia kierowcy',
              when: { on: ['driverValidityInclusive'] },
              meaning: 'Uprawnienie „ważne do 12.09” obowiązuje przez cały 12.09 i wygasa z początkiem 13.09 — tak samo jak u pojazdu.',
            },
          ],
        },
        {
          kind: 'text',
          when: { off: ['vehicleTypeWarning'] },
          text: 'Auto, którego typ nie obsłuży zlecenia, odpada z rekomendacji z powodem „typ auta nie obsłuży zlecenia”.',
        },
        {
          kind: 'note',
          tone: 'system',
          when: { on: ['cabotageByTms'] },
          text: 'Kabotaż i „cooling off” liczy TMS — rekomendacje nie pokazują własnych ostrzeżeń o kabotażu ani punktu „Kabotaż”.',
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────── warianty
    {
      id: 'warianty',
      title: 'Warianty: dodawanie i zwalnianie',
      blocks: [
        {
          kind: 'text',
          text: 'Wariant to wstępne wskazanie auta do zlecenia. Zlecenie może mieć kilka wariantów; Team Leader wybiera z nich to, które obsadzi. Przy obsadzeniu pozostałe warianty zlecenia zwalniają się same, a obsadzone auto traci kandydatury w innych zleceniach w tym samym czasie.',
        },
        {
          kind: 'task',
          title: 'Dodaj wariant z karty zlecenia',
          intro: 'Gdy znasz auto i nie potrzebujesz listy rekomendacji.',
          steps: [
            'Na karcie zlecenia w sekcji akcji rozwiń „Sugerowane pojazdy”.',
            'Wybierz auto z listy (auta niedostępne są w osobnej grupie).',
            'Kliknij „Dodaj wariant”.',
          ],
          outcome: 'Wariant pojawia się w „Wariantach obsadzenia” z Twoim nazwiskiem i godziną, a zlecenie przechodzi do „Wstępnie planowane”.',
        },
        {
          kind: 'task',
          title: 'Zwolnij wariant',
          steps: [
            'W sekcji „Warianty obsadzenia” kliknij „Zwolnij” przy aucie (na karcie pojazdu ikona „Usuń wariant”).',
            'W oknie „Zwolnić wariant …?” wpisz „Powód (opcjonalny)” i kliknij „Zwolnij wariant”.',
          ],
          outcome: 'Gdy zlecenie straci ostatni wariant, wraca do „Gotowe do planowania”. Powód zostaje w historii.',
        },
        {
          kind: 'text',
          when: { on: ['variantValidity'] },
          text: 'Nowy wariant ma termin ważności (domyślnie najbliższa 17:00, co najmniej godzinę po dodaniu). Termin zmienia autor wariantu albo Team Leader przyciskiem „Zmień termin”, z powodem. Wariant po terminie dostaje oznaczenie „po terminie”, ale NIE zwalnia się sam. Gdy to samo auto jest w nachodzącym czasie wariantem albo obsadzeniem innego zlecenia, wariant ma znacznik konfliktu — to ostrzeżenie, nie blokada.',
        },
        {
          kind: 'image',
          when: { on: ['variantValidity'] },
          image: { guide: 'variantValidity', step: 1 },
          caption: 'Termin ważności przy wariancie.',
        },
        {
          kind: 'text',
          when: { on: ['variantWithinWindows'] },
          text: 'Wariantu nie dodasz, gdy realizacja pewnie nie mieści się w oknach zlecenia: przy świeżych danych z telematyki kierowca nie zdąży nawet z pełną pulą ulg. Przycisk „Wariant” jest wtedy wyszarzony z powodem. Przy braku danych albo wyniku niepewnym wariant powstaje z ostrzeżeniem i wpisem w historii.',
        },
        {
          kind: 'image',
          when: { on: ['variantWithinWindows'] },
          image: { guide: 'variantWithinWindows', step: 1 },
          caption: '„Wariant” wyszarzony przy pewnym spóźnieniu.',
        },
        {
          kind: 'text',
          when: { on: ['runningOrderNotVariant'] },
          text: 'Kandydatura auta do zlecenia, które TMS ma już w realizacji albo zrealizowane, nie liczy się jako wariant w harmonogramach — w tabeli pod osią ma podpis „zlecenie w realizacji w TMS — nie jest wariantem”. Nic nie zwalnia się samo.',
        },
      ],
    },

    // ──────────────────────────────────────── obsadzenie, odpięcie, Trade
    {
      id: 'obsadzenie-odpiecie-trade',
      title: 'Obsadzenie, odpięcie i przekazanie do Trade',
      blocks: [
        {
          kind: 'text',
          when: { off: ['assignButtonForPlanner'] },
          text: 'Planista nie obsadza — przycisku „Obsadź” u planisty nie ma. Obsadza Team Leader FTL i administracja, wybierając spośród dodanych wariantów (szczegóły w rozdziale Team Leader FTL).',
        },
        {
          kind: 'text',
          when: { on: ['assignButtonForPlanner'] },
          text: 'Planista nie obsadza: przycisk „Obsadź” widzisz nieaktywny, z podpowiedzią „Obsadza Team Leader” — przy rekomendacjach, na karcie zlecenia i w wierszu listy. Obsadza Team Leader FTL i administracja.',
        },
        {
          kind: 'task',
          title: 'Zgłoś wniosek o odpięcie auta',
          intro: 'Gdy obsadzone auto nie może jechać i zlecenie trzeba oddać do puli.',
          steps: [
            'Otwórz kartę obsadzonego zlecenia.',
            'W sekcji akcji wybierz, dokąd zlecenie ma wrócić („Po odpięciu zlecenie wróci do”).',
            'Wpisz „Powód wniosku (wymagany)” — co najmniej 10 znaków.',
            'Kliknij „Wnioskuj o odpięcie”.',
          ],
          outcome:
            'Auto zostaje przy zleceniu, a na górze karty pojawia się baner „Wniosek o odpięcie czeka na decyzję”. Przełożony akceptuje albo odrzuca z powodem; do tego czasu możesz go wycofać („Wycofaj wniosek”). Nic nie wykonuje się samo.',
        },
        {
          kind: 'text',
          when: { on: ['unassignBeforeDeparture'] },
          text: 'Dopóki auto nie wyruszyło na załadunek, odpinasz sam przyciskiem „Odepnij” z powodem — wniosek jest potrzebny dopiero po wyjeździe.',
        },
        {
          kind: 'task',
          title: 'Przekaż zlecenie do Trade',
          intro: 'Gdy flota nie ma auta i zlecenie ma kupić przewoźnik.',
          steps: [
            'Na karcie zlecenia w stanie „Gotowe do planowania” albo „Wstępnie planowane” rozwiń „Zmień stan”.',
            'Wybierz „Przekazane do Trade” i wpisz powód, jeśli jest wymagany.',
            'Kliknij „Zmień”.',
          ],
          outcome:
            'Zlecenie przechodzi do działu Trade (dzwonek Trade). Spółka bez kont giełdowych ma tę opcję wyszarzoną z powodem. Obsadzone zlecenie przekazuje Team Leader (wyjątki opisuje rozdział Team Leadera FTL). Dalsza praca — rozdział Trade.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Na giełdę wystawia wyłącznie człowiek z działu Trade. Do TMS nic nie trafia automatem — obsadzenie z aplikacji zapisuje się u nas, a TMS uzupełnia planista albo Team Leader.',
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────── alerty
    {
      id: 'alerty',
      title: 'Alerty przy zleceniach',
      blocks: [
        {
          kind: 'text',
          text: 'Alerty pokazują się na górze karty zlecenia, w dzwonku działu i w kolumnie „Znaczniki”. Żaden alert niczego nie zmienia sam — obsadzenie, warianty i harmonogram zostają, dopóki człowiek nie zdecyduje.',
        },
        {
          kind: 'terms',
          title: 'Rodzaje alertów',
          items: [
            {
              term: 'Zmiana w TMS po obsadzeniu',
              meaning: 'Klient dopisał punkt, przesunął godzinę albo zmienił LDM przy zleceniu z naszym autem albo wariantami.',
            },
            {
              term: 'Podejrzenie anulacji',
              meaning: 'Obsadzenie zniknęło z TMS (pojazd znika albo status wraca do W). To sygnał do potwierdzenia, nie fakt.',
            },
            {
              term: 'Alert przeplanowania',
              meaning:
                'Przy obsadzonym zleceniu z załadunkiem w 48 h auto nie zdąży, kierowca albo pojazd są niedostępni albo jest wyraźnie lepsze wolne auto.',
              when: { off: ['replanAlertMasterOnly'] },
            },
            {
              term: 'Alert przeplanowania',
              meaning: 'Widzi go i przyjmuje do wiadomości Team Leader FTL (i administratorzy), bo to oni obsadzają — planista go nie dostaje.',
              when: { on: ['replanAlertMasterOnly'] },
            },
            { term: 'Straciło pojazd', meaning: 'Zlecenie miało auto i je straciło — czerwona ikona w „Znacznikach”.' },
            {
              term: '„Jutro załadunek, bez auta”',
              when: { on: ['loadingTomorrowAlert'] },
              meaning: 'Codziennie o 15:00 system wskazuje zlecenia z jutrzejszym załadunkiem, które nadal nie mają auta ani przewoźnika.',
            },
            {
              term: '„Zmiana pojazdu w TMS po obsadzeniu”',
              when: { on: ['vehicleChangesFromTms'] },
              meaning: 'Kartoteka pojazdów odświeża się co godzinę; zmiana auta obsadzonego albo wariantu (przepięta naczepa, wyłączenie z eksploatacji) daje alert.',
            },
            {
              term: '„Inny pojazd w TMS niż u nas”',
              when: { on: ['vehicleSwapFromTms'] },
              meaning:
                'Ktoś zamienił w TMS auto przy obsadzonym zleceniu. Gdy auto przyszło z TMS, import przepina obsadzenie; gdy wybrano je w aplikacji albo numer pasuje do kilku aut — ważny alert do decyzji.',
            },
            {
              term: '„Zlecenie anulowane w TMS”',
              when: { on: ['tmsCancellations'] },
              meaning:
                'TMS podaje listę anulowanych zleceń; zgodny numer daje alert na karcie, znacznik na liście i powiadomienie. Pojazd, warianty i stan zostają — decydujesz Ty.',
            },
            {
              term: 'Alerty ze źródłem Transporeon',
              when: { on: ['transporeonImport'] },
              meaning:
                'Komunikaty zleceniodawcy z Transporeonu przy zleceniu z TMS o tym samym numerze transportu: „Zmiana w Transporeonie” (okno załadunku lub rozładunku, miejsce, trasa, waga), „Anulowane w Transporeonie — do decyzji” (te same przyciski co anulacja w TMS: „Potwierdzam anulację” albo „To nie anulacja”) i ponowne wystawienie pod tym samym numerem — możliwy duplikat w banku. Karta ma sekcję „Transporeon” tylko do odczytu. W TMS nic się nie zmienia — sprawdź, czy klient poprawił zlecenie także tam.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'system',
          when: { on: ['importantAlerts'] },
          text: 'Ważne alerty (anulacja i zmiany w TMS po obsadzeniu, zmiana pojazdu, jutrzejszy załadunek bez auta, utrata pojazdu, pewne przeplanowanie) są czerwone i na górze; dzwonek ma czerwony licznik tylko przy ważnym. Zwykłe podpowiedzi przeplanowania są zwinięte i szare.',
        },
        {
          kind: 'task',
          title: 'Obsłuż alert o zmianie w TMS',
          steps: [
            'Otwórz zlecenie z dzwonka albo z filtra „Znaczniki”.',
            'Przeczytaj baner na górze karty: co zmieniło się w TMS.',
            'Sprawdź, czy obsadzone auto i warianty nadal pasują (np. „Pokaż rekomendowane pojazdy”).',
            'Kliknij „Przyjmuję do wiadomości”.',
          ],
          outcome: 'Potwierdzenie to tylko wpis w historii — nie zmienia obsadzenia.',
        },
        {
          kind: 'task',
          title: 'Rozstrzygnij podejrzenie anulacji',
          steps: [
            'Upewnij się u klienta albo w TMS, czy zlecenie faktycznie jest anulowane.',
            'Na banerze kliknij „Potwierdzam anulację” albo „To nie anulacja”.',
            'Wpisz powód (co najmniej 10 znaków) w polu „Powód potwierdzenia anulacji” albo „Dlaczego to nie anulacja” i kliknij „Zwolnij pojazd i zamknij zlecenie” albo „Zapisz, że to nie anulacja”.',
          ],
          outcome:
            '„Potwierdzam anulację” zdejmuje auto, zwalnia warianty i przenosi zlecenie do „Nieaktualne”. Do TMS nic nie idzie. „To nie anulacja” tylko zapisuje decyzję.',
        },
      ],
    },

    // ─────────────────────────────────────────────────── flota: samochody, naczepy
    {
      id: 'flota',
      title: 'Samochody i naczepy',
      menu: ['cars', 'trailers'],
      blocks: [
        {
          kind: 'text',
          text: '„Samochody” i „Naczepy” to kartoteka floty z TMS: numer, typ, ładowność, kierowca, naczepa, spółka, dostępność. Filtry „Tylko dostępne”, „Z kierowcą”, „Bez naczep i wózków”, wyszukiwarka i „Tylko moje pojazdy” zawężają listę. Kliknięcie wiersza otwiera kartę pojazdu.',
        },
        {
          kind: 'image',
          image: { shot: 'cars' },
          caption: 'Lista samochodów.',
        },
        {
          kind: 'text',
          when: { on: ['fleetHideArchived'] },
          text: 'Listy floty i harmonogram nie pokazują aut oznaczonych w TMS jako poza eksploatacją. Przełącznik „Pokaż także archiwalne” je przywraca.',
        },
        {
          kind: 'text',
          when: { on: ['operationalLabel'] },
          text: 'Filtr i kolumny mówią „W eksploatacji” / „poza eksploatacją” zamiast „Sprawne” — ta flaga z TMS nie mówi o usterce.',
        },
        {
          kind: 'text',
          when: { off: ['operationalLabel'] },
          text: 'Filtr „Sprawne” pokazuje auta, które TMS oznacza jako w eksploatacji — nie mówi, czy auto ma usterkę.',
        },
        {
          kind: 'text',
          when: { on: ['fleetCorrections'] },
          text: 'Auto z ręczną korektą kartoteki ma na liście znacznik „≠ TMS” przy kierowcy albo naczepie.',
        },
      ],
    },

    // ─────────────────────────────────────────────────────── karta pojazdu
    {
      id: 'karta-pojazdu',
      title: 'Karta pojazdu',
      blocks: [
        {
          kind: 'text',
          text: 'Karta pojazdu pokazuje kartotekę auta, uprawnienia z TMS, harmonogram (obsadzenia pełnym paskiem, warianty przerywanym) i rekomendowane zlecenia dla tego pojazdu — kierunek odwrotny do rekomendacji przy zleceniu.',
        },
        {
          kind: 'image',
          image: { shot: 'car-card' },
          caption: 'Karta pojazdu — u góry podsumowanie i harmonogram; rekomendowane zlecenia są niżej na karcie.',
        },
        {
          kind: 'task',
          title: 'Znajdź zlecenie dla wolnego auta',
          steps: [
            'Otwórz kartę pojazdu (z „Samochodów” albo z harmonogramu floty).',
            'W sekcji „Rekomendowane zlecenia dla pojazdu” kliknij „Pokaż rekomendowane zlecenia”.',
            'Przejrzyj zlecenia bez auta w horyzoncie 7 dni: dolot, koszt dolotu, marża, zapas, „Na jakiej podstawie”.',
            'Kliknij „Wariant” przy wybranym zleceniu — albo „Usuń wariant”, jeśli auto już jest wariantem i ma zrezygnować.',
          ],
          outcome:
            'Lista pomija zlecenia kolidujące z obsadzeniami auta i z załadunkiem przed jego zwolnieniem. Ta sama para ma w obu kierunkach te same punkty „Na jakiej podstawie”.',
        },
        {
          kind: 'task',
          title: 'Uzupełnij ADR pojazdu',
          when: { on: ['manualAdr'] },
          intro: 'TMS ani telematyka nie podają ADR — wpisuje go człowiek.',
          steps: [
            'Na karcie pojazdu znajdź pole „ADR”.',
            'Wybierz „tak”, „nie” albo „nie wpisano” i opcjonalnie wpisz powód.',
            'Kliknij „Zapisz”.',
          ],
          outcome: '„Nie” wyklucza pojazd ze zleceń z ADR i blokuje obsadzenie; „nie wpisano” daje tylko ostrzeżenie. Zmiana trafia do dziennika działań.',
        },
        {
          kind: 'list',
          title: 'Nowy układ karty pojazdu',
          when: { on: ['vehicleCardPrototype'] },
          items: [
            'Pasek podsumowania i baner alertu na górze; alert to te same wpisy co na karcie zlecenia i potwierdza się go tam.',
            'Harmonogram z wierszami „W trakcie / Zaplanowane / Zakończone”, skalą 1–28 dni, widokiem uproszczonym i pełnym i oknami załadunku i rozładunku.',
            'Wiersz „Aktywność kierowcy” z prawdziwej osi tachografu; przyszłość z obsadzeń jako szacunek.',
            'Rekomendowane zlecenia z „Oceną” (mieści się / z zastrzeżeniem / brak danych / nie zdąży) i „Wolny od” nad tabelą.',
            'Kolory: w realizacji niebieskie, zaplanowane zielone, zrealizowane szare, pomarańcz tylko dla ostrzeżeń.',
          ],
        },
        {
          kind: 'text',
          when: { on: ['vehicleVariantsLane'] },
          text: 'Warianty stoją w osobnym wierszu „Warianty” między „Zaplanowane” i „Zakończone”, a skala ma też widok „3 dni”.',
        },
        {
          kind: 'text',
          when: { on: ['vehiclePreviewSegments'] },
          text: 'Najechanie na zlecenie w rekomendacjach pokazuje je na osi pojazdu rozpisane na odcinki: dolot, załadunek, przejazd z przerwami, rozładunek. Zwarta oś nad tabelą trzyma się góry ekranu.',
        },
        {
          kind: 'text',
          when: { on: ['vehicleTodoPanel'] },
          text: 'Panel „Do zrobienia” pod podsumowaniem zbiera to, co wiadomo o aucie: poza eksploatacją albo niedostępne, alerty zleceń, brak kierowcy, brak zaplanowanego zlecenia, warianty do decyzji, terminy z kartoteki w ciągu 30 dni — z przejściem do zlecenia albo rekomendacji.',
        },
        {
          kind: 'text',
          when: { on: ['vehicleAssignmentHistory'] },
          text: 'Sekcja „Historia pojazdu” pokazuje obsadzenia, zmiany auta, odpięcia oraz dodane i zwolnione warianty: kiedy, które zlecenie, kto (albo TMS, reguła) i z jakim powodem.',
        },
        {
          kind: 'text',
          when: { on: ['carChangeHistory'] },
          text: 'Zwinięta sekcja „Historia zmian z TMS”: zmiany zestawu, eksploatacji, dostępności, typu, ładowności, kierowcy, opiekuna i ADR — kiedy, z czego na co i skąd.',
        },
        {
          kind: 'text',
          when: { on: ['trimbleTerminalOnCard'] },
          text: 'W kartotece jest wiersz z numerem terminala w kabinie z kartoteki pojazdów TMS — pod tym numerem kontaktuje się kierowcę. Brak w TMS to „—”.',
        },
        {
          kind: 'text',
          when: { on: ['staleTachographPast'] },
          text: 'Gdy oś tachografu jest starsza niż 24 h, wiersz „Aktywność kierowcy” nadal rysuje zapisaną przeszłość z podpisem „ostatni odczyt … — starszy niż 24 h”; „Pokaż ostatni odczyt” przenosi oś do dnia odczytu. Teraźniejszości z osi wtedy nie ma.',
        },
        {
          kind: 'task',
          title: 'Popraw kierowcę albo naczepę w kartotece',
          when: { on: ['fleetCorrections'] },
          intro: 'Gdy TMS ma nieaktualnego kierowcę, zestaw albo termin, a Ty wiesz, jak jest naprawdę.',
          steps: [
            'Na karcie pojazdu zjedź do sekcji „Kierowca i zestaw”.',
            'Kliknij „Zmień” przy kierowcy albo naczepie (przy uprawnieniu — „Zmień termin”).',
            'Wybierz nową wartość, przeczytaj skutki (warianty i obsadzenia, których to dotyczy) i wpisz powód.',
            'Zapisz.',
          ],
          outcome:
            'Wartość różna od TMS ma znacznik „≠ TMS” z przyciskiem „przywróć z TMS”. Import jej nie nadpisuje; gdy TMS zrówna się z korektą, zamyka się sama. Korygujesz swoje auta; Team Leader — wszystkie.',
        },
        {
          kind: 'image',
          when: { on: ['fleetCorrections'] },
          image: { guide: 'fleetCorrections', step: 1 },
          caption: 'Sekcja „Kierowca i zestaw” na karcie pojazdu.',
        },
      ],
    },

    // ─────────────────────────────────────────────────── harmonogram floty
    {
      id: 'harmonogram-floty',
      title: 'Harmonogram floty',
      menu: ['fleet-schedule'],
      blocks: [
        {
          kind: 'text',
          text: '„Harmonogram floty” pokazuje plan wszystkich aut na osi dni: obsadzenia pełnym paskiem, warianty przerywanym, na trzech liniach — zrealizowane, w realizacji, zaplanowane. Pasek to szacowana zajętość (dolot, załadunek, przejazd, rozładunek), a okno klienta jest zaznaczone obok. To tylko odczyt.',
        },
        {
          kind: 'image',
          image: { shot: 'fleet-schedule' },
          caption: 'Harmonogram floty.',
        },
        {
          kind: 'task',
          title: 'Sprawdź plan auta w harmonogramie floty',
          steps: [
            'Otwórz „Harmonogram floty” i włącz „Tylko moje pojazdy”, jeśli chcesz widzieć tylko swoje auta.',
            'Znajdź auto i najedź na pasek zlecenia — dymek pokaże zlecenie, odcinki i szacowany koniec.',
            'Kliknij auto, żeby przejść do jego karty i rekomendowanych zleceń.',
          ],
        },
        {
          kind: 'list',
          title: 'Układ harmonogramu',
          when: { on: ['fleetScheduleMockup'] },
          items: [
            'Skala 1 / 3 / 7 / 14 dni (domyślnie 3).',
            'Kolumna „Wolny od · gdzie”: kiedy i gdzie auto zwalnia się po ostatnim obsadzeniu; kolejność według czasu zwolnienia.',
            'Filtr „Pokaż: z alertem / z wariantem”, żetony przy aucie (alert, bez kierowcy), odcinek spóźnienia na osi.',
            'Liczniki wolnych aut teraz, za 6, 12 i 24 h nad osią i luki w planie z odnośnikiem do rekomendacji.',
          ],
        },
        {
          kind: 'text',
          when: { on: ['fleetMatchingOrders'] },
          text: 'Kolumna „Pasujące” mówi, ile zleceń bez auta w horyzoncie rekomendacji pasuje do auta (ta sama reguła co rekomendowane zlecenia, dolot szacunkiem, z godziną przeliczenia). Luka na osi pokazuje, ile z nich ma załadunek w tej luce, a kliknięcie otwiera ich listę.',
        },
        {
          kind: 'text',
          when: { on: ['occupiedUntilTmsPlan'] },
          text: 'Za paskiem może stać stonowany odcinek „wg planu TMS”, gdy plan TMS przy punktach kończy się później niż szacunek; dymek podaje „Zajęty do: … (źródło)”.',
        },
        {
          kind: 'text',
          when: { on: ['ganttWindowBrackets'] },
          text: 'Okna klienta są rysowane jako nawiasy: Z nad paskiem (załadunek), R pod paskiem (rozładunek), przy kilku punktach Z1, R1, R2. Nawias jest czerwony, gdy szacowany załadunek albo rozładunek wypada poza oknem. Te same nazwy ma oś czasu na karcie zlecenia.',
        },
        {
          kind: 'image',
          when: { on: ['ganttWindowBrackets'] },
          image: { guide: 'ganttWindowBrackets', step: 3 },
          caption: 'Nawiasy okien klienta przy zleceniach w harmonogramie floty.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Pasek to szacunek, nie plan kierowcy. Kolizję z oknem traktuj jako sygnał do sprawdzenia, nie jako fakt.',
        },
      ],
    },

    // ─────────────────────────────────────────────────────────── kierowcy
    {
      id: 'kierowcy',
      title: 'Kierowcy',
      menu: ['drivers'],
      blocks: [
        {
          kind: 'text',
          text: '„Kierowcy” to kartoteka z TMS uzupełniona danymi z eco-trucka: pojazd, zezwolenie na pracę, najbliższa nieobecność, pozostały czas jazdy (dziś / tydzień / 2 tygodnie). Filtry „Tylko aktywni”, „Tylko z pojazdem” i „Zezwolenie po terminie” pomagają znaleźć braki.',
        },
        {
          kind: 'terms',
          title: 'Dane o kierowcy',
          items: [
            {
              term: '„Dostępny od”',
              meaning:
                'Szacunek z czasów jazdy z eco-trucka (odczyt nie starszy niż 24 h) i nieobecności z kartoteki, bez ulg. Brak danych to brak danych, nie „dostępny”.',
            },
            { term: 'Nieobecność', meaning: 'Urlop albo L4 z kartoteki TMS — jedyny pewny werdykt „nie zdąży”.' },
            {
              term: 'Liczniki ulg',
              when: { on: ['ecoTruckReliefs'] },
              meaning: 'Karta kierowcy pokazuje wykorzystane wydłużenia jazdy do 10 h i skrócone odpoczynki z eco-trucka, z czasem odczytu.',
            },
            {
              term: '„Zezwolenie na pracę do”',
              when: { on: ['driverValidityInclusive'] },
              meaning: 'Termin liczy się włącznie: zezwolenie ważne do 12.09 obowiązuje przez cały 12.09. Tak samo liczą filtry „Zezwolenie po terminie” i „termin za N dni”.',
            },
            {
              term: '„ADR”',
              when: { on: ['manualAdr'] },
              meaning: 'Pole na karcie kierowcy wpisywane ręcznie, jak przy pojeździe: „nie” wyklucza auto ze zleceń z ADR, brak wpisu ostrzega.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Czasy jazdy przychodzą z eco-trucka automatycznie; nieobecności — z kartoteki TMS. Planowane urlopy wpisuje się w TMS.',
        },
      ],
    },
  ],
};
